/**
 * /assistants — Agent Studio home (hidden: AgentStudioRoute, per-user flag).
 *
 * "My assistants" grid + company template gallery (clone CTA) + New Assistant
 * modal. Everything goes through the agentStudio callable; nothing here is
 * reachable without users/{uid}.agentStudioEnabled.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box, Heading, Text, SimpleGrid, HStack, VStack, Button, IconButton, Badge,
  Spinner, useToast, useColorModeValue, Menu, MenuButton, MenuList, MenuItem,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, Input, Textarea, Switch, useDisclosure,
} from '@chakra-ui/react';
import { FiPlus, FiMessageSquare, FiEdit2, FiMoreVertical, FiTrash2, FiCopy, FiMail, FiCpu, FiSettings } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { callAgentStudio } from '../../hooks/useAssistants';

function AssistantCard({ agent, isTemplate, onChat, onEdit, onClone, onDelete, cardBg, cardBorder, subtle }) {
  const isGmail = (agent.connector_keys || []).includes('gmail');
  return (
    <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5} display="flex" flexDirection="column">
      <HStack justify="space-between" align="start" mb={2}>
        <HStack spacing={2}>
          <Box color={isGmail ? 'red.400' : 'purple.400'}>{isGmail ? <FiMail size={20} /> : <FiCpu size={20} />}</Box>
          <Heading size="sm" noOfLines={1}>{agent.name}</Heading>
        </HStack>
        {!isTemplate && (
          <Menu>
            <MenuButton as={IconButton} icon={<FiMoreVertical />} size="xs" variant="ghost" aria-label="More" />
            <MenuList>
              <MenuItem icon={<FiTrash2 />} color="red.400" onClick={() => onDelete(agent)}>Delete</MenuItem>
            </MenuList>
          </Menu>
        )}
      </HStack>
      <Text fontSize="sm" color={subtle} noOfLines={3} flex="1">{agent.description || 'No description'}</Text>
      <HStack mt={3} spacing={2} flexWrap="wrap">
        <Badge colorScheme={isTemplate ? 'blue' : 'green'} fontSize="2xs">{isTemplate ? 'Template' : 'Yours'}</Badge>
        <Badge fontSize="2xs" variant="outline">{agent.type}</Badge>
        {(agent.connector_keys || []).map((k) => <Badge key={k} colorScheme="red" fontSize="2xs">{k}</Badge>)}
      </HStack>
      <HStack mt={4} spacing={2}>
        {isTemplate ? (
          <Button size="sm" colorScheme="blue" leftIcon={<FiCopy />} onClick={() => onClone(agent)} flex="1">
            Use this template
          </Button>
        ) : (
          <>
            <Button size="sm" colorScheme="blue" leftIcon={<FiMessageSquare />} onClick={() => onChat(agent)} flex="1">Chat</Button>
            <Button size="sm" variant="outline" leftIcon={<FiEdit2 />} onClick={() => onEdit(agent)}>Customize</Button>
          </>
        )}
      </HStack>
    </Box>
  );
}

export default function AssistantsHomePage() {
  const history = useHistory();
  const toast = useToast();
  const { currentUser } = useAuth();
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtle = useColorModeValue('gray.600', 'gray.400');
  const pageBg = useColorModeValue('gray.50', 'gray.900');

  const [loading, setLoading] = useState(true);
  const [assistants, setAssistants] = useState([]);
  const [busy, setBusy] = useState(false);
  const createModal = useDisclosure();
  const [form, setForm] = useState({ name: '', description: '', instructions: '', gmail: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await callAgentStudio('listAssistants');
      setAssistants(data.assistants || []);
    } catch (err) {
      toast({ title: 'Could not load assistants', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const mine = assistants.filter((a) => a.owner_uid === currentUser?.uid && !a.is_template);
  const templates = assistants.filter((a) => a.is_template);

  const handleClone = async (template) => {
    setBusy(true);
    try {
      const { agent } = await callAgentStudio('cloneTemplate', { templateId: template.id });
      toast({ title: `Created "${agent.name}"`, description: 'Your own copy — customize it or start chatting.', status: 'success', duration: 4000 });
      history.push(`/assistants/${agent.id}/chat`);
    } catch (err) {
      toast({ title: 'Clone failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Name required', status: 'warning', duration: 3000 });
      return;
    }
    setBusy(true);
    try {
      const { agent } = await callAgentStudio('createAssistant', {
        assistant: {
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          instructions: form.instructions.trim() || undefined,
          connectorKeys: form.gmail ? ['gmail'] : [],
        },
      });
      createModal.onClose();
      setForm({ name: '', description: '', instructions: '', gmail: true });
      toast({ title: `Created "${agent.name}"`, status: 'success', duration: 3000 });
      history.push(`/assistants/${agent.id}/edit`);
    } catch (err) {
      toast({ title: 'Create failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (agent) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${agent.name}" and all its chats, skills, and files? This cannot be undone.`)) return;
    try {
      await callAgentStudio('deleteAssistant', { id: agent.id });
      toast({ title: 'Deleted', status: 'info', duration: 3000 });
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 6000 });
    }
  };

  return (
    <Box minH="100vh" bg={pageBg} p={6}>
      <Box maxW="1100px" mx="auto">
        <HStack justify="space-between" mb={1}>
          <Heading size="lg">AI Assistants</Heading>
          <HStack>
            {currentUser?.SuperAdmin && (
              <Button size="sm" variant="ghost" leftIcon={<FiSettings />} onClick={() => history.push('/agents/settings')}>
                Studio admin
              </Button>
            )}
            <Button size="sm" colorScheme="blue" leftIcon={<FiPlus />} onClick={createModal.onOpen}>New assistant</Button>
          </HStack>
        </HStack>
        <Text fontSize="sm" color={subtle} mb={6}>
          Your personal AI agents — customize their instructions, teach them skills, give them knowledge, and chat.
        </Text>

        {loading ? (
          <Box textAlign="center" py={20}><Spinner size="xl" /></Box>
        ) : (
          <VStack align="stretch" spacing={8}>
            <Box>
              <Heading size="md" mb={3}>My assistants</Heading>
              {mine.length === 0 ? (
                <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={8} textAlign="center">
                  <Text color={subtle} mb={3}>You don&apos;t have an assistant yet. Start from the Gmail Assistant template below, or create one from scratch.</Text>
                </Box>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {mine.map((a) => (
                    <AssistantCard
                      key={a.id}
                      agent={a}
                      isTemplate={false}
                      onChat={(ag) => history.push(`/assistants/${ag.id}/chat`)}
                      onEdit={(ag) => history.push(`/assistants/${ag.id}/edit`)}
                      onDelete={handleDelete}
                      cardBg={cardBg} cardBorder={cardBorder} subtle={subtle}
                    />
                  ))}
                </SimpleGrid>
              )}
            </Box>

            <Box>
              <Heading size="md" mb={3}>Templates</Heading>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {templates.map((a) => (
                  <AssistantCard
                    key={a.id}
                    agent={a}
                    isTemplate
                    onClone={handleClone}
                    cardBg={cardBg} cardBorder={cardBorder} subtle={subtle}
                  />
                ))}
              </SimpleGrid>
              {templates.length === 0 && <Text fontSize="sm" color={subtle}>No templates published yet.</Text>}
            </Box>
          </VStack>
        )}
      </Box>

      <Modal isOpen={createModal.isOpen} onClose={createModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New assistant</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Name</FormLabel>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Pipeline Copilot" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Description</FormLabel>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What is it for?" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Instructions (you can refine later)</FormLabel>
                <Textarea
                  value={form.instructions}
                  onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                  placeholder="How should it behave? Tone, priorities, rules…"
                  rows={4}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <Switch isChecked={form.gmail} onChange={(e) => setForm({ ...form, gmail: e.target.checked })} mr={3} />
                <FormLabel fontSize="sm" mb={0}>Give it access to my Gmail (your own mailbox only)</FormLabel>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={createModal.onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleCreate} isLoading={busy}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
